import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/patterns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DollarSign,
  Plus,
  Edit,
  Trash2,
  Copy,
  GripVertical,
  GitBranch,
  Info,
  Tag,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DependencyGraph } from '../components/dependency-graph';
import { useToast } from '@/lib/hooks/use-toast';
import { useModelStore } from '../store/model-store';
import { Variable } from '../types/model.types';

import { VariableForm } from '../components/variable-form';
import { LookupTableManager } from '../components/lookup-table-manager';
import { ModelSimulator } from '../components/model-simulator';
import { VariableType } from '@/types';

const MODEL_TYPE_LABELS: Record<string, string> = {
  STANDARD: 'Estandar',
  SUBSCRIPTION: 'Suscripcion',
  BUNDLE: 'Bundle / Paquete',
  GOOD_BETTER_BEST: 'Bueno-Mejor-Optimo',
  CUSTOMER_SPECIFIC: 'Especifico por Cliente',
  TCO_ROI: 'TCO / ROI',
  PROMOTIONAL: 'Promocional',
  USAGE_BASED: 'Basado en Uso',
  COST_PLUS: 'Costo + Margen',
  DYNAMIC: 'Dinamico',
  LEASING: 'Leasing',
  MANAGED_SERVICE: 'Servicio Gestionado',
  TIERED_VOLUME: 'Volumen Escalonado',
  FREEMIUM: 'Freemium',
  PROJECT_BASED: 'Por Proyecto',
  HYBRID: 'Hibrido',
  AUCTION: 'Subasta / Licitación',
};

const getVariableTypeLabel = (type: VariableType): string => {
  const labels: Record<VariableType, string> = {
    [VariableType.NUMBER]: 'Numero',
    [VariableType.PERCENTAGE]: 'Porcentaje',
    [VariableType.CURRENCY]: 'Moneda',
    [VariableType.TEXT]: 'Texto',
    [VariableType.BOOLEAN]: 'Booleano',
    [VariableType.CALCULATED]: 'Calculada',
    [VariableType.ENUM]: 'Enum',
    [VariableType.TIERED]: 'Escalonada',
    [VariableType.LOOKUP]: 'Lookup',
  };
  return labels[type] || type;
};

const getVariableTypeBadgeVariant = (type: VariableType): string => {
  switch (type) {
    case VariableType.CALCULATED:
      return 'info';
    case VariableType.ENUM:
      return 'secondary';
    case VariableType.TIERED:
      return 'warning';
    case VariableType.LOOKUP:
      return 'secondary';
    default:
      return 'default';
  }
};

const ROLE_OPTIONS = [
  { value: 'NONE', label: '— Sin rol —', color: '' },
  { value: 'COST_INPUT', label: 'Costo Entrada', color: 'text-primary' },
  { value: 'COST_BASE_MARGIN', label: 'Costo Base Margen', color: 'text-info' },
  { value: 'UNIT_COST', label: 'Costo Unitario', color: 'text-primary' },
  { value: 'QUANTITY', label: 'Cantidad', color: 'text-violet-600' },
  { value: 'UNIT_PRICE', label: 'Precio Unit.', color: 'text-alerta' },
  { value: 'TOTAL_PRICE', label: 'Total Línea', color: 'text-alerta' },
  { value: 'MARGIN_PCT', label: 'Margen %', color: 'text-ok' },
  { value: 'MARGIN', label: 'Margen (monto)', color: 'text-ok' },
  { value: 'TAX', label: 'Impuesto (monto)', color: 'text-alerta' },
];

function SortableVariableRow({
  variable,
  onEdit,
  onDelete,
  onRoleChange,
  takenRoles,
}: {
  variable: Variable;
  onEdit: (v: Variable) => void;
  onDelete: (id: string) => void;
  onRoleChange: (variableId: string, role: string | null) => void;
  takenRoles: Record<string, string>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: variable.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={
        variable.variableType === VariableType.CALCULATED
          ? 'bg-ok-suave'
          : variable.variableType === VariableType.TIERED
            ? 'bg-purple-50/50'
            : variable.variableType === VariableType.ENUM
              ? 'bg-info-suave'
              : ''
      }
    >
      <TableCell className="w-8">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </TableCell>
      <TableCell className="font-mono text-muted-foreground">{variable.order}</TableCell>
      <TableCell className="font-medium">{variable.name}</TableCell>
      <TableCell className="font-mono">{variable.code}</TableCell>
      <TableCell>
        <Badge variant={getVariableTypeBadgeVariant(variable.variableType) as any}>
          {getVariableTypeLabel(variable.variableType)}
        </Badge>
      </TableCell>
      <TableCell className="font-mono text-sm max-w-[200px] truncate">
        {variable.formula
          ? variable.formula
          : variable.variableType === VariableType.ENUM && variable.options
            ? `${variable.options.length} opciones`
            : variable.variableType === VariableType.TIERED && variable.tiers
              ? `${variable.tiers.length} niveles`
              : variable.variableType === VariableType.LOOKUP && variable.lookupTable
                ? variable.lookupTable.name
                : '-'}
      </TableCell>
      <TableCell>{variable.isGlobal && <Badge variant="outline">Global</Badge>}</TableCell>
      <TableCell>
        <SearchableSelect
          value={variable.priceRole || 'NONE'}
          onValueChange={(val) => onRoleChange(variable.id, val === 'NONE' ? null : val)}
          className={`w-[130px] h-7 text-xs ${
            (variable.priceRole as string) === 'COST_INPUT' || variable.priceRole === 'UNIT_COST'
              ? 'border-info-borde bg-info-suave '
              : (variable.priceRole as string) === 'COST_BASE_MARGIN'
                ? 'border-info-borde bg-info-suave '
                : variable.priceRole === 'QUANTITY'
                  ? 'border-violet-300 bg-violet-50 dark:bg-violet-950/30'
                  : variable.priceRole === 'UNIT_PRICE' || variable.priceRole === 'TOTAL_PRICE'
                    ? 'border-alerta-borde bg-alerta-suave '
                    : variable.priceRole === 'MARGIN' ||
                        (variable.priceRole as string) === 'MARGIN_PCT'
                      ? 'border-ok-borde bg-ok-suave '
                      : variable.priceRole === 'TAX'
                        ? 'border-alerta-borde bg-alerta-suave '
                        : ''
          }`}
          options={ROLE_OPTIONS.map((opt) => {
            const taken = opt.value !== 'NONE' && takenRoles[opt.value];
            return {
              value: opt.value,
              label: taken ? `${opt.label} (${taken})` : opt.label,
            };
          })}
          placeholder="— Sin rol —"
        />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={() => onEdit(variable)}>
                <Edit className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Editar nombre, tipo, formula o configuración de esta variable
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="sm" variant="ghost" onClick={() => onDelete(variable.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Eliminar esta variable del modelo</TooltipContent>
          </Tooltip>
        </div>
      </TableCell>
    </TableRow>
  );
}

export function ModelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVariable, setEditingVariable] = useState<Variable | null>(null);
  const [showDependencyGraph, setShowDependencyGraph] = useState(false);
  const [deleteVariableId, setDeleteVariableId] = useState<string | null>(null);

  const {
    currentModel,
    variables,
    lookupTables,
    isLoading,
    fetchModel,
    fetchVariables,
    updateVariable,
    deleteVariable,
    cloneModel,
    reorderVariables,
  } = useModelStore();

  useEffect(() => {
    if (id) {
      fetchModel(id);
      fetchVariables(id);
    }
  }, [id]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedVariables = [...variables].sort((a, b) => a.order - b.order);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedVariables.findIndex((v) => v.id === active.id);
    const newIndex = sortedVariables.findIndex((v) => v.id === over.id);
    const reordered = arrayMove(sortedVariables, oldIndex, newIndex);
    const variableIds = reordered.map((v) => v.id);

    try {
      await reorderVariables(id!, variableIds);
      toast({ title: 'Orden actualizado', variant: 'success' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleCreateVariable = () => {
    setEditingVariable(null);
    setIsDialogOpen(true);
  };

  const handleEditVariable = (variable: Variable) => {
    setEditingVariable(variable);
    setIsDialogOpen(true);
  };

  const handleDeleteVariable = (variableId: string) => {
    setDeleteVariableId(variableId);
  };

  // Price roles: mapa de roles tomados → nombre de variable
  const takenRoles: Record<string, string> = {};
  (currentModel?.variables || []).forEach((v: Variable) => {
    if (v.priceRole) takenRoles[v.priceRole] = v.name;
  });

  const handleRoleChange = async (variableId: string, role: string | null) => {
    try {
      await updateVariable(id!, variableId, { priceRole: role } as any);
      toast({ title: 'Rol actualizado' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const confirmDeleteVariable = async () => {
    if (!deleteVariableId) return;
    try {
      await deleteVariable(id!, deleteVariableId);
      toast({ title: 'Variable eliminada', variant: 'success' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setDeleteVariableId(null);
    }
  };

  const handleClone = async () => {
    try {
      const cloned = await cloneModel(id!);
      toast({ title: 'Modelo clonado', variant: 'success' });
      navigate(`/pricing-models/${cloned.id}`);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (!currentModel && isLoading) {
    return (
      <AppLayout>
        <div className="container mx-auto p-8">
          <p>Cargando...</p>
        </div>
      </AppLayout>
    );
  }

  if (!currentModel) {
    return (
      <AppLayout>
        <div className="container mx-auto p-8">
          <p>Modelo no encontrado</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-8">
        <div className="space-y-6">
          {/* El encabezado de Cotiza (artefacto «Modelo · detalle», 19-oct). */}
          <div data-tour="model-detail-info">
            <PageHeader
              volver={{ texto: 'Modelos', onClick: () => navigate('/pricing-models') }}
              modulo="cotiza"
              etiqueta="Cotiza · Modelo"
              icono={DollarSign}
              titulo={
                <span className="inline-flex flex-wrap items-center gap-3">
                  {currentModel.name}
                  {currentModel.type && (
                    <Badge variant="outline" className="text-sm font-normal tracking-normal">
                      {MODEL_TYPE_LABELS[currentModel.type] || currentModel.type}
                    </Badge>
                  )}
                </span>
              }
              subtitulo={`Código: ${currentModel.code}`}
              acciones={
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        data-tour="model-detail-clone"
                        variant="outline"
                        onClick={handleClone}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Clonar Modelo
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Crear una copia de este modelo con todas sus variables y formulas
                    </TooltipContent>
                  </Tooltip>
                  <Badge variant={currentModel.isActive ? 'success' : 'default'}>
                    {currentModel.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
              }
            />
          </div>

          {/* Description */}
          {currentModel.description && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
                  <p className="text-muted-foreground">{currentModel.description}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Model Info */}
          <div data-tour="model-detail-margins" className="grid grid-cols-1 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Variables</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold">{variables.length}</p>
              </CardContent>
            </Card>
          </div>

          {/* Roles de Cotización */}
          {variables.length > 0 && (
            <Card className="border-2 border-primary/20">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Tag className="w-4 h-4" />
                  Roles en Cotización
                </CardTitle>
                <CardDescription>
                  Marcá qué variable del modelo cumple cada rol. Esto le dice al sistema cómo leer
                  tu modelo para mostrar costos, márgenes, impuestos y precios en la cotización de
                  forma coherente.
                </CardDescription>
                <details className="mt-3 text-xs bg-muted/50 rounded-md p-3 border border-muted">
                  <summary className="cursor-pointer font-medium text-foreground">
                    📖 ¿Qué significa cada rol? (click para ver guía completa)
                  </summary>
                  <div className="mt-3 space-y-3 text-muted-foreground leading-relaxed">
                    <div>
                      <span className="font-semibold text-primary">🔵 Costo Entrada</span> — La
                      variable donde entra el costo crudo del proveedor (ej. <code>VALOR_FOB</code>,{' '}
                      <code>COSTO_ALMACEN</code>). Sin flete, sin impuestos, sin margen. Es el input
                      de costo que el vendedor recibe.
                    </div>
                    <div>
                      <span className="font-semibold text-info">🔷 Costo Base Margen</span> — La
                      base sobre la que se aplica el <strong>% de margen</strong>. En modelos
                      simples es igual al costo de entrada. En importaciones suele ser{' '}
                      <code>costo + flete + GA + impuestos</code> (todo lo que pagaste antes de
                      agregar tu margen). Si no la marcás, el sistema usa Costo Entrada.
                    </div>
                    <div>
                      <span className="font-semibold text-primary">🔵 Costo Unitario</span> —
                      Alias/sinónimo de Costo Entrada para modelos viejos. Preferí Costo Entrada.
                    </div>
                    <div>
                      <span className="font-semibold text-violet-700">🟣 Cantidad</span> — La
                      variable que guarda la cantidad del ítem. Siempre debe ser{' '}
                      <code>CANTIDAD</code>.
                    </div>
                    <div>
                      <span className="font-semibold text-alerta">🟡 Precio Unitario</span> — El
                      precio final por unidad que ve el cliente. Es el resultado del cálculo.
                    </div>
                    <div>
                      <span className="font-semibold text-alerta">🟡 Total Línea</span> — Precio
                      final × cantidad. Es lo que suma al Gran Total de la cotización.
                    </div>
                    <div>
                      <span className="font-semibold text-ok">🟢 Margen %</span> — El{' '}
                      <strong>porcentaje</strong> de margen que aplica el vendedor (ej.{' '}
                      <code>MARGEN_PCT = 25</code>). Es un <em>input/setting</em> del modelo — lo
                      que configura el vendedor al crear la cotización.
                    </div>
                    <div>
                      <span className="font-semibold text-ok">🟢 Margen (monto)</span> — El{' '}
                      <strong>monto</strong> de ganancia en plata que el vendedor se lleva (ej.{' '}
                      <code>MONTO_MARGEN</code>). Es el <em>resultado</em> del cálculo. El sistema
                      usa este monto como fuente de verdad del margen real:{' '}
                      <code>Margen Real = Margen (monto) / Costo Base Margen × 100</code>.
                    </div>
                    <div>
                      <span className="font-semibold text-alerta">🟠 Impuesto (monto)</span> —
                      Montos pasantes que no son margen (IVA, provisiones, retenciones). Se restan
                      del "arriba del costo" al mostrar margen real para no inflarlo.
                    </div>
                    <div className="pt-2 border-t border-muted-foreground/20">
                      <strong className="text-foreground">
                        Ejemplo — modelo "Importaciones + venta":
                      </strong>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        <li>
                          <code>VALOR_FOB</code> → Costo Entrada
                        </li>
                        <li>
                          <code>COSTO_ALMACEN</code> (FOB + flete + GA) → Costo Base Margen
                        </li>
                        <li>
                          <code>CANTIDAD</code> → Cantidad
                        </li>
                        <li>
                          <code>MARGEN_PCT</code> (el % que el vendedor configura, ej. 25) → Margen
                          %
                        </li>
                        <li>
                          <code>MONTO_MARGEN</code> (= COSTO_ALMACEN × MARGEN_PCT / 100) → Margen
                          (monto)
                        </li>
                        <li>
                          <code>MONTO_IMPUESTO</code> → Impuesto (monto)
                        </li>
                        <li>
                          <code>PRECIO_UNIT</code> → Precio Unitario
                        </li>
                        <li>
                          <code>TOTAL</code> → Total Línea
                        </li>
                      </ul>
                    </div>
                    <div className="pt-2 text-[11px] italic">
                      Los modelos que no marquen roles siguen funcionando con heurísticos basados en
                      nombres conocidos (<code>COSTO_ALMACEN</code>, <code>MONTO_MARGEN</code>,
                      etc.), pero es menos confiable. Marcá los roles explícitamente cuando puedas.
                    </div>
                  </div>
                </details>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    {
                      role: 'COST_INPUT' as const,
                      label: 'Costo Entrada',
                      desc: 'Costo crudo del proveedor (ej: VALOR_FOB)',
                      color: 'bg-info-suave border-info-borde',
                    },
                    {
                      role: 'COST_BASE_MARGIN' as const,
                      label: 'Costo Base Margen',
                      desc: 'Base sobre la que se calcula el % de margen',
                      color: 'bg-info-suave border-info-borde',
                    },
                    {
                      role: 'UNIT_PRICE' as const,
                      label: 'Precio Unitario',
                      desc: 'Precio final por unidad',
                      color: 'bg-alerta-suave border-alerta-borde',
                    },
                    {
                      role: 'TOTAL_PRICE' as const,
                      label: 'Total Línea',
                      desc: 'Total del item (qty × precio)',
                      color: 'bg-alerta-suave border-alerta-borde',
                    },
                    {
                      role: 'MARGIN_PCT' as const,
                      label: 'Margen %',
                      desc: 'Porcentaje de margen aplicado (input, ej: MARGEN_PCT = 25)',
                      color: 'bg-ok-suave border-ok-borde',
                    },
                    {
                      role: 'MARGIN' as const,
                      label: 'Margen (monto)',
                      desc: 'Monto de ganancia en plata (resultado, ej: MONTO_MARGEN)',
                      color: 'bg-ok-suave border-ok-borde',
                    },
                    {
                      role: 'TAX' as const,
                      label: 'Impuesto (monto)',
                      desc: 'Monto pasante (IVA, provisiones, etc.)',
                      color: 'bg-alerta-suave border-alerta-borde',
                    },
                  ].map(({ role, label, desc, color }) => {
                    const currentVar = variables.find((v) => v.priceRole === role);
                    return (
                      <div key={role} className={`p-3 rounded-lg border ${color}`}>
                        <label className="text-xs font-semibold block mb-1.5">{label}</label>
                        <Select
                          value={currentVar?.id || 'NONE'}
                          onValueChange={async (varId) => {
                            // Si había otra variable con este rol, quitarle el rol
                            if (currentVar) {
                              await updateVariable(id!, currentVar.id, { priceRole: null } as any);
                            }
                            // Asignar el rol a la nueva variable
                            if (varId !== 'NONE') {
                              // Si la variable ya tenía otro rol, se reemplaza
                              await updateVariable(id!, varId, { priceRole: role } as any);
                            }
                            // Refrescar
                            await fetchModel(id!);
                            toast({ title: `${label} actualizado`, variant: 'success' });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs bg-white">
                            <SelectValue placeholder="Sin asignar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="NONE">— Sin asignar —</SelectItem>
                            {variables.map((v) => (
                              <SelectItem key={v.id} value={v.id}>
                                {v.name} ({v.code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-muted-foreground mt-1">{desc}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Variables */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Variables del Modelo</CardTitle>
                  <CardDescription>
                    Define las variables de entrada, formulas calculadas y tablas de búsqueda.
                    Arrastra las filas para reordenar. Las variables calculadas (verde) usan
                    formulas que referencian otras variables por su código.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        data-tour="model-detail-graph"
                        variant="outline"
                        onClick={() => setShowDependencyGraph(!showDependencyGraph)}
                      >
                        <GitBranch className="w-4 h-4 mr-2" />
                        {showDependencyGraph ? 'Ocultar Grafo' : 'Ver Grafo'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Visualizar el grafo de dependencias entre variables calculadas
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button data-tour="model-detail-new-var" onClick={handleCreateVariable}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nueva Variable
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Agregar una nueva variable al modelo (numero, porcentaje, formula, enum, etc.)
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {variables.length === 0 ? (
                <div data-tour="model-detail-variables" className="text-center py-8">
                  <p className="text-muted-foreground">No hay variables configuradas</p>
                  <Button onClick={handleCreateVariable} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Primera Variable
                  </Button>
                </div>
              ) : (
                <>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <Table data-tour="model-detail-variables">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead className="w-16">Orden</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Código</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Detalle</TableHead>
                          <TableHead className="w-16">Global</TableHead>
                          <TableHead className="w-24">Rol</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <SortableContext
                          items={sortedVariables.map((v) => v.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {sortedVariables.map((variable) => (
                            <SortableVariableRow
                              key={variable.id}
                              variable={variable}
                              onEdit={handleEditVariable}
                              onDelete={handleDeleteVariable}
                              onRoleChange={handleRoleChange}
                              takenRoles={takenRoles}
                            />
                          ))}
                        </SortableContext>
                      </TableBody>
                    </Table>
                  </DndContext>
                  {showDependencyGraph && variables.length > 0 && (
                    <div className="mt-6">
                      <DependencyGraph
                        variables={variables}
                        onAddVariable={() => {
                          setEditingVariable(null);
                          setIsDialogOpen(true);
                        }}
                        onEditVariable={(v) => {
                          setEditingVariable(v);
                          setIsDialogOpen(true);
                        }}
                        onDeleteVariable={handleDeleteVariable}
                        onRefresh={() => fetchModel(id!)}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Simulador (el explicador con IA se sacó junto con el asistente, lote D G1) */}
          {variables.length > 0 && <ModelSimulator variables={variables} />}

          {/* Lookup Tables */}
          <LookupTableManager modelId={id!} lookupTables={lookupTables} />
        </div>
      </div>

      {/* Delete Variable Confirmation */}
      <AlertDialog
        open={!!deleteVariableId}
        onOpenChange={(open) => !open && setDeleteVariableId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará esta variable de forma permanente. Si otras variables dependen de ella,
              sus fórmulas podrían dejar de funcionar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteVariable}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Variable Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVariable ? 'Editar Variable' : 'Nueva Variable'}</DialogTitle>
            <DialogDescription>
              {editingVariable
                ? 'Modifica los datos de la variable'
                : 'Crea una nueva variable para el modelo'}
            </DialogDescription>
          </DialogHeader>
          <VariableForm
            modelId={id!}
            variable={editingVariable || undefined}
            availableVariables={variables
              .map((v) => v.code)
              .filter((code): code is string => code !== undefined)}
            lookupTables={lookupTables}
            onSuccess={() => {
              setIsDialogOpen(false);
              setEditingVariable(null);
              fetchVariables(id!);
            }}
            onCancel={() => setIsDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

export default ModelDetailPage;
